// FontPicker — 화면 표시 폰트(개인 설정). 내정보 화면에 들어간다.
//
// 배경(2026-08-20): 게이트웨이엔 웹폰트가 하나도 없어 직원 PC마다 다른 폰트로 보였다.
// 🔴 화면 전용이다. 내보내기(PDF/DOCX)에는 적용하지 않는다 —
//    브라우저 PDF 인쇄는 폰트를 파일에 서브셋 임베딩하므로 그건 '배포'가 되고,
//    상용 폰트는 PDF 임베딩을 제한하는 EULA 가 있다.
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { authAPI } from '../api/client';
import {
  FONTS, CUSTOM_PREFIX, DEFAULT_FONT,
  applyFont, storeFont, cssStack, loadFontAssets, detectLocalFonts, isFontInstalled,
} from '../utils/fontPrefs';

const PREVIEW = '아워박스 AI 게이트웨이 · 출고 12,345건 ABCabc';

export default function FontPicker({ value, onSaved, mobile }) {
  const initial = value || DEFAULT_FONT;
  const [sel, setSel] = useState(initial);
  const [customName, setCustomName] = useState(
    initial.startsWith(CUSTOM_PREFIX) ? initial.slice(CUSTOM_PREFIX.length) : ''
  );
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  // 🔴 안내 문구는 반드시 이 헬퍼로 띄운다.
  //    예전엔 성공 경로의 finally 에서만 setTimeout 을 걸었는데, 검증 실패는 조기 return 이라
  //    자기 타이머를 안 걸었다 → **직전 저장이 걸어둔 타이머가 새 메시지를 지워버렸다**
  //    (저장 후 4초 안에 다시 누르면 안내가 스쳐 지나감. 2026-08-20 e2e 로 발견).
  const msgTimer = useRef(null);
  const flash = useCallback((m) => {
    if (msgTimer.current) { clearTimeout(msgTimer.current); msgTimer.current = null; }
    setMsg(m);
    if (m) msgTimer.current = setTimeout(() => { setMsg(null); msgTimer.current = null; }, 4000);
  }, []);
  useEffect(() => () => { if (msgTimer.current) clearTimeout(msgTimer.current); }, []);

  // 미리보기용으로 후보 폰트 CSS를 미리 받아 둔다(선택 전에도 제대로 보이도록).
  useEffect(() => { FONTS.forEach((f) => loadFontAssets(f.key)); }, []);

  // 이 PC에 실제로 설치된 폰트만 추린다(캔버스 폭 비교). 없는 폰트를 권하면 헛수고가 된다.
  const localFonts = useMemo(() => detectLocalFonts(), []);
  const customOk = !!customName.trim() && isFontInstalled(customName.trim());

  const pick = (key) => { setSel(key); flash(null); applyFont(key); };
  const pickCustom = (name) => {
    setCustomName(name);
    setSel(CUSTOM_PREFIX + name);
    flash(null);
    if (name.trim()) applyFont(CUSTOM_PREFIX + name.trim());
  };

  const save = async () => {
    const v = sel.startsWith(CUSTOM_PREFIX) ? CUSTOM_PREFIX + customName.trim() : sel;
    if (v.startsWith(CUSTOM_PREFIX) && !customName.trim()) {
      flash({ ok: false, text: '폰트 이름을 입력해 주세요.' }); return;
    }
    setSaving(true);
    try {
      await authAPI.updateMe({ ui_font: v });
      storeFont(v);
      applyFont(v);
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...u, ui_font: v }));
      flash({ ok: true, text: '폰트가 저장됐습니다. 다른 기기에서도 같은 폰트로 보입니다.' });
      if (onSaved) onSaved(v);
    } catch (e) {
      flash({ ok: false, text: e.response?.data?.detail || '저장 실패' });
    } finally {
      setSaving(false);   // 문구 지우기는 flash() 가 관리한다(여기서 또 걸면 타이머가 겹친다)
    }
  };

  const reset = () => { setSel(DEFAULT_FONT); setCustomName(''); applyFont(DEFAULT_FONT); flash(null); };

  const row = (active) => ({
    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
    padding: mobile ? '10px' : '11px 12px', marginBottom: '7px', textAlign: 'left',
    border: `1px solid ${active ? '#4F46E5' : '#e5e7eb'}`,
    background: active ? '#eef2ff' : '#fff',
    borderRadius: '9px', cursor: 'pointer', boxSizing: 'border-box',
  });

  return (
    <div>
      <p style={{ fontSize: '12.5px', color: '#6b7280', lineHeight: 1.6, marginBottom: '12px' }}>
        게이트웨이 전 화면에 적용됩니다. 계정에 저장되므로 다른 PC에서 로그인해도 따라옵니다.
      </p>

      {FONTS.map((f) => {
        const active = sel === f.key;
        return (
          <button key={f.key} type="button" onClick={() => pick(f.key)} style={row(active)}>
            <span aria-hidden="true" style={{
              width: '16px', height: '16px', flexShrink: 0, borderRadius: '50%',
              border: `5px solid ${active ? '#4F46E5' : '#d1d5db'}`, background: '#fff', boxSizing: 'border-box',
            }} />
            <span style={{ minWidth: 0, flex: 1 }}>
              <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>
                {f.label}
                <span style={{ marginLeft: '7px', fontSize: '11px', fontWeight: 400, color: '#9ca3af' }}>{f.note}</span>
              </span>
              <span style={{
                display: 'block', marginTop: '3px', fontSize: mobile ? '14px' : '15px', color: '#374151',
                fontFamily: cssStack(f.key), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{PREVIEW}</span>
            </span>
          </button>
        );
      })}

      {/* ── 내 PC에 설치된 폰트 ─────────────────────────────────── */}
      <div style={{ marginTop: '16px', paddingTop: '15px', borderTop: '1px solid #f1f5f9' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937', marginBottom: '3px' }}>내 PC에 설치된 폰트 쓰기</div>
        <p style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.6, marginBottom: '10px' }}>
          폰트 파일을 올리지 않고 <b>이름만</b> 지정합니다. 내 PC에 이미 설치된 폰트로 내 화면에만 그려지므로,
          서버가 폰트를 저장하거나 전송하지 않습니다.
        </p>

        {localFonts.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
            {localFonts.map((n) => {
              const active = sel === CUSTOM_PREFIX + n;
              return (
                <button key={n} type="button" onClick={() => pickCustom(n)} style={{
                  padding: '5px 10px', fontSize: '12.5px', cursor: 'pointer', borderRadius: '999px',
                  border: `1px solid ${active ? '#4F46E5' : '#d1d5db'}`,
                  background: active ? '#eef2ff' : '#fff', color: active ? '#4338ca' : '#374151',
                  fontFamily: cssStack(CUSTOM_PREFIX + n),
                }}>{n}</button>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={customName}
            onChange={(e) => pickCustom(e.target.value)}
            placeholder="폰트 이름 직접 입력 (예: 함초롬돋움)"
            style={{
              flex: '1 1 220px', minWidth: 0, minHeight: '38px', padding: '8px 10px',
              border: `1px solid ${customName.trim() && !customOk ? '#f59e0b' : '#d1d5db'}`,
              borderRadius: '6px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
            }} />
        </div>
        {customName.trim() && (
          <div style={{ marginTop: '8px' }}>
            <div style={{ fontSize: '11.5px', color: customOk ? '#059669' : '#b45309', marginBottom: '4px' }}>
              {customOk
                ? '✓ 이 PC에 설치돼 있습니다.'
                : '⚠ 이 PC에서 찾을 수 없는 폰트입니다. 저장해도 기본 폰트로 보입니다(이름 철자를 확인해 주세요).'}
            </div>
            <div style={{
              fontSize: '15px', color: '#374151', padding: '8px 10px', background: '#f8fafc',
              border: '1px solid #e5e7eb', borderRadius: '7px',
              fontFamily: cssStack(CUSTOM_PREFIX + customName.trim()),
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{PREVIEW}</div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
        <button type="button" onClick={save} disabled={saving} style={{
          padding: '9px 18px', background: saving ? '#a5b4fc' : '#4F46E5', color: '#fff', border: 'none',
          borderRadius: '7px', fontSize: '13px', fontWeight: 600, cursor: saving ? 'default' : 'pointer',
        }}>{saving ? '저장 중...' : '저장'}</button>
        <button type="button" onClick={reset} style={{
          padding: '9px 16px', background: '#fff', color: '#4b5563', border: '1px solid #d1d5db',
          borderRadius: '7px', fontSize: '13px', cursor: 'pointer',
        }}>기본값으로</button>
      </div>

      {msg && (
        <div role="status" style={{ marginTop: '10px', fontSize: '12.5px', color: msg.ok ? '#059669' : '#dc2626' }}>{msg.text}</div>
      )}

      <p style={{ marginTop: '12px', fontSize: '11.5px', color: '#9ca3af', lineHeight: 1.6 }}>
        내보내기(PDF·Word)에는 적용되지 않습니다 — 문서 파일에는 폰트가 함께 저장될 수 있어
        저작권 문제를 피하려고 고정 폰트를 씁니다.
      </p>
    </div>
  );
}
